import {RefObject, useEffect, useRef} from 'react';
import {IElementHtml} from '../incase-domain/elements/html';
import {IRunFile} from '../incase-domain/proptypes/runfile';
import {convertToEm} from '../incase-domain/utils/emConverter';
import {presCustomVarsParser} from '../util/slide.utils';
import {useLayout} from './useLayout';

interface UseHtmlResult {
    containerRef: RefObject<HTMLDivElement>;
}

export function useHtmlIframe(element: IElementHtml, runfile: IRunFile, onClick?: () => void): UseHtmlResult {
    const {text} = element;
    const {mobileView} = useLayout();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        if (!text || !containerRef.current) return undefined;

        if (!iframeRef.current) {
            const iframe = document.createElement('iframe');
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.style.display = 'block';
            iframe.style.overflow = 'auto';

            if (onClick) {
                iframe.addEventListener('load', () => {
                    iframe.contentWindow?.document.addEventListener('click', onClick);
                });
            }

            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(iframe);
            iframeRef.current = iframe;
        }

        const processedText = convertToEm(presCustomVarsParser(text, runfile));

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

        if (iframeDoc) {
            const container = containerRef.current;
            container.dataset.htmlLoaded = 'false';
            iframe.addEventListener(
                'load',
                () => {
                    Promise.resolve(iframe.contentDocument?.fonts?.ready).then(() => {
                        container.dataset.htmlLoaded = 'true';
                    });
                },
                {once: true}
            );

            iframeDoc.open();
            iframeDoc.write(processedText);
            iframeDoc.close();
            if (iframeDoc.body) {
                iframeDoc.body.style.margin = '0';
            }
        }

        return onClick
            ? () => {
                  iframe.contentWindow?.document.removeEventListener('click', onClick);
              }
            : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (element.coverSlide) {
            if (iframe) iframe.style.height = '100%';
            return undefined;
        }
        if (iframe && mobileView) {
            iframe.addEventListener('load', () => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });

            const resize = new ResizeObserver(() => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });
            resize.observe(document.body);
        }
        return undefined;
    }, [mobileView, element.coverSlide]);

    return {containerRef};
}
